/**
 * OWNEXP Slice 2/4 — zentrales Übersetzungsregister für technische States
 * in Owner-Sprache (DNA 06 §7, Version simo.status-copy.v1).
 *
 * Regeln (DC-31/DC-34, AP-30):
 * - Der Owner-Name ist Primärlabel; der Rohcode bleibt sekundär (Drilldown/Tooltip).
 * - Ein Rohcode OHNE Registereintrag wird als solcher NICHT als Primärlabel
 *   gerendert (AP-30) — `statusCopy` liefert dann null und die Fläche entscheidet
 *   über den Fallback „Unbekannter Zustand" + Rohcode im Tooltip.
 * - Eine Sprache: Deutsch, mit Umlauten.
 */

export const STATUS_COPY_VERSION = "simo.status-copy.v1";

export interface StatusCopyEintrag {
  /** Owner-Primärlabel (kurz, verständlich). */
  label: string;
  /** Erklärungssatz: was bedeutet der Zustand operativ (DC-63: Ereignis + Bedingung). */
  erklaerung: string;
}

const REGISTER: Record<string, StatusCopyEintrag> = {
  // ── Queue-/Work-Item-States ──
  READY: { label: "Startbereit", erklaerung: "Alle Vorbedingungen erfüllt — wartet auf einen freien Writer-Slot." },
  READY_TO_RESUME: { label: "Bereit zur Fortsetzung", erklaerung: "Der vorherige Writer ist beendet. SIMO wartet auf einen neuen Writer-Lease." },
  RUNNING: { label: "Läuft gerade", erklaerung: "Ein Writer arbeitet mit aktiver Lease an diesem Item." },
  WAIT_OWNER: { label: "Wartet auf deine Entscheidung", erklaerung: "SIMO braucht eine Owner-Entscheidung (Human Gate), bevor es weiterläuft." },
  WAIT_HUMAN: { label: "Wartet auf eine Person", erklaerung: "Eine menschliche Freigabe wird benötigt, bevor es weitergeht." },
  WAIT_RESOURCE: { label: "Wartet auf freie Kapazität", erklaerung: "Die benötigte Ressource ist belegt — SIMO startet automatisch, sobald frei." },
  WAIT_DEPENDENCY: { label: "Wartet auf eine Vorbedingung", erklaerung: "Ein anderes Item muss zuerst abgeschlossen werden." },
  WAIT_EXECUTOR: { label: "Wartet auf einen Agenten", erklaerung: "SIMO hat gerade keinen ausführenden Agenten (Worker) für diese Aufgabe — es läuft automatisch weiter, sobald einer verfügbar ist. Kein Handgriff nötig." },
  WAIT_EXTERNAL: { label: "Wartet auf ein externes Ereignis", erklaerung: "Etwas außerhalb von SIMO muss eintreffen (z. B. ein Callback oder Fenster)." },
  BLOCKED: { label: "Blockiert", erklaerung: "Eine Abhängigkeit oder ein Gate verhindert die Ausführung." },
  PARKED: { label: "Geparkt", erklaerung: "Bewusst zurückgestellt — kein Writer arbeitet daran." },
  COMPLETED: { label: "Abgeschlossen", erklaerung: "Ziel erreicht und über Events belegt." },
  FAILED: { label: "Fehlgeschlagen", erklaerung: "Der Lauf wurde mit Fehler beendet — Details im Drilldown." },
  CANCELLED: { label: "Abgebrochen", erklaerung: "Der Lauf wurde abgebrochen (z. B. Lease-Ende ohne Abschluss)." },
  "(unverwaltet)": { label: "Unverwaltet", erklaerung: "Dieses Item läuft außerhalb der verwalteten Queue." },

  // ── Verify-/Verdict-Sprache ──
  VERIFIED: { label: "Geprüft", erklaerung: "Unabhängig verifiziert." },
  "CONDITIONAL GO": { label: "Freigabe mit Vorbehalt", erklaerung: "Go, solange die genannte Bedingung erfüllt/behoben ist." },
  "NO-GO": { label: "Abgelehnt", erklaerung: "Der Prüfpunkt ist gescheitert — nicht abnehmen." },
  FAIL_CLOSED: { label: "Gesperrt (sicherheitshalber)", erklaerung: "Im Zweifel wurde bewusst gesperrt statt geraten." },
  ROLLED_BACK: { label: "Zurückgenommen", erklaerung: "Die Änderung wurde zurückgenommen." },

  // ── Datenzustände (DC-12/13: nie 0, nie vermischt) ──
  UNAVAILABLE: { label: "Nicht abrufbar", erklaerung: "Die Quelle ist gerade nicht verbunden — kein Ersatzwert, keine 0." },
  UNKNOWN: { label: "Nicht gemessen", erklaerung: "Es gibt noch keine Messung für diesen Wert." },
  INACTIVE: { label: "Inaktiv", erklaerung: "Bewusst abgeschaltet." },

  // ── Ressourcen-/Scope-Vokabular (R-Q-7: ein Lease-Vokabular) ──
  "scope.writer": { label: "Schreibrecht auf einem Repository", erklaerung: "Exklusives Schreibrecht (Writer-Lease) für einen klar begrenzten Bereich." },
  "build.heavy": { label: "Schwerer Build", erklaerung: "Ressourcenklasse für lange Build-/Testläufe." },
};

/** Owner-Label für einen Rohcode — oder null, wenn nicht registriert (AP-30). */
export function statusLabel(rohcode: string | null | undefined): string | null {
  if (!rohcode) return null;
  const eintrag = REGISTER[rohcode];
  return eintrag?.label ?? null;
}

/** Erklärungssatz für einen Rohcode — oder null. */
export function statusErklaerung(rohcode: string | null | undefined): string | null {
  if (!rohcode) return null;
  const eintrag = REGISTER[rohcode];
  return eintrag?.erklaerung ?? null;
}

/** Vollständiger Eintrag — oder null (AP-30-Fallback obliegt der Fläche). */
export function statusCopy(rohcode: string | null | undefined): StatusCopyEintrag | null {
  if (!rohcode) return null;
  return REGISTER[rohcode] ?? null;
}
